import { Component, type ErrorInfo, type ReactNode, useMemo, useState } from 'react'
import { toast } from 'sonner'

import BrokerContext from 'components/context/BrokerContext'
import BrokersMemberContext from 'components/context/BrokersMemberContext'
import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import MemberIchraConfig from 'components/MemberIchraConfig'
import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import MemberNotes from 'components/reusable/MemberNotes'
import { brokersEmployees, brokersEmployers } from 'rails_routes'
import { Banner, Breadcrumbs, SolidAlertTriangleIcon } from 'slab'
import {
  useGetAccessRequests,
  useGetBrokersMembersMemberId,
  usePostAccessRequests,
} from 'utils/web_api'
import { type BrokersBroker } from 'utils/web_api.schemas'

import BrokersMembersEnrollments from './BrokersMembersEnrollments'
import BrokersMembersEnrollmentsSummary from './BrokersMembersEnrollmentsSummary'
import BrokersMembersPlanYearSelector from './BrokersMembersPlanYearSelector'
import BrokersMembersSummaryInfo from './BrokersMembersSummaryInfo'
import BrokersMembersUploadsView from './BrokersMembersUploadsView'
import BrokersMembersViewReasonForAccess from './BrokersMembersViewReasonForAccess'
import CardsMembersView from './cards/CardsMembersView'
import CoveredParticipantsView, {
  COVERED_PARTICIPANTS_VIEW_FEATURES,
} from './covered_participants/CoveredParticipantsView'
import FinancialAccountsMembersView from './financial_accounts/FinancialAccountsMembersView'
import MemberIchraAffordability from './MemberIchraAffordability'
import MemberInformation from './MemberInformation'
import QualifyingLifeEventsView from './qualifying_life_events/QualifyingLifeEventsView'
import StripeCardsMembersView from './stripe_cards/StripeCardsMembersView'
import TasksMembersView from './tasks/TasksMembersView'

export const BROKERS_MEMBERS_VIEW_FEATURES = {
  BREADCRUMBS: 'breadcrumbs',
  COVERED_PARTICIPANTS: 'covered_participants',
  COVERED_PARTICIPANTS_ADD_DEPENDENT: COVERED_PARTICIPANTS_VIEW_FEATURES.ADD_DEPENDENT,
  COVERED_PARTICIPANTS_EDIT_PARTICIPANT: COVERED_PARTICIPANTS_VIEW_FEATURES.EDIT_PARTICIPANT,
  ENROLLMENTS: 'enrollments',
  ICHRA_AFFORDABILITY: 'ichra_affordability',
  MEMBER_ICHRA_CONFIG: 'member_ichra_config',
  MEMBER_INFORMATION: 'member_information',
  MEMBER_SUMMARY: 'member_summary',
  NOTES: 'notes',
  QUALIFYING_LIFE_EVENTS: 'qualifying_life_events',
  REQUIRE_ACCESS_GRANT: 'require_access_grant',
  STRIPE_CARDS: 'stripe_cards',
  CARDS: 'cards',
  TASKS: 'tasks',
  UPLOADS: 'uploads',
} as const

export type BrokersMembersViewFeature = typeof BROKERS_MEMBERS_VIEW_FEATURES[
  keyof typeof BROKERS_MEMBERS_VIEW_FEATURES
]

interface Props {
  children: ReactNode
  planYear?: number
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

// Error Boundary component for debugging
class BrokersMembersViewErrorBoundary extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError (error: Error): State {
    return { hasError: true, error, errorInfo: null }
  }

  componentDidCatch (error: Error, errorInfo: ErrorInfo) {
    console.error('BrokersMembersView Error Boundary caught an error:', {
      error,
      errorInfo,
      planYear: this.props.planYear
    })
    this.setState({ error, errorInfo })
  }

  render () {
    if (this.state.hasError) {
      return (
        <div className='p-6'>
          <Banner type='critical' title='Something went wrong'>
            <div className='space-y-2 text-sm'>
              <div>
                <strong>Error:</strong> {this.state.error?.message}
              </div>
              <details>
                <summary className='cursor-pointer font-semibold'>Stack Trace</summary>
                <pre className='whitespace-pre-wrap text-xs bg-gray-100 p-2 rounded mt-2'>
                  {this.state.error?.stack}
                </pre>
              </details>
              <details>
                <summary className='cursor-pointer font-semibold'>Component Stack</summary>
                <pre className='whitespace-pre-wrap text-xs bg-gray-100 p-2 rounded mt-2'>
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
              <button
                onClick={() => window.location.reload()}
                className='mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700'
              >
                Reload Page
              </button>
            </div>
          </Banner>
        </div>
      )
    }

    return this.props.children
  }
}

interface BrokersMembersViewProps {
  accessRequestResourceName: string
  memberId: string
  memberName: string
  employerId: string
  employerName: string
  enabledFeatures: BrokersMembersViewFeature[]
  isTieredAllowancesEmployer: boolean
  currentBroker: BrokersBroker
  currentUserId: string
  stripePublishableKey?: string
  visaImagePath?: string
  // NOTE: This is used to gate certain features for Thatch admins in the broker portal that Brokers from other
  // brokerages don't have access to.
  isUserThatchAdmin: boolean
  isUserThatchWorkflowAdmin: boolean
}

const BrokersMembersViewImpl = (props: BrokersMembersViewProps) => {
  const {
    memberId,
    memberName,
    employerId,
    employerName,
    enabledFeatures,
    isTieredAllowancesEmployer = false,
    isUserThatchAdmin,
  } = props

  // Initialize selectedPlanYear from URL parameters if available
  const [selectedPlanYear, setSelectedPlanYear] = useState<number | undefined>(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const urlSelectedYear = urlParams.get('selected_year')
    if (urlSelectedYear) {
      const parsedYear = parseInt(urlSelectedYear, 10)
      if (!isNaN(parsedYear)) {
        return parsedYear
      }
    }
    return undefined
  })

  const {
    data: member,
    error,
    isLoading,
  } = useGetBrokersMembersMemberId(memberId, {
    query: {
      enabled: !!memberId,
      refetchOnWindowFocus: false,
    },
  })

  // Memoize availablePlanYears to prevent array recreation on every render
  const availablePlanYears = useMemo(() => {
    return member?.ichra_configs?.map(({ plan_year: planYear }) => planYear) || []
  }, [member?.ichra_configs])

  // We don't need to show a loading state here because the rest of the components handle loading
  // states with skeletons that provide a better user experience.

  if (error) {
    return (
      <Banner title='Error loading member' type='critical'>
        {(error as GrapeAPIErrorType)?.message ||
          'An error occurred while loading the member.'}
      </Banner>
    )
  }

  const employerIchraConfig = member?.employer_ichra_configs?.find(
    ({ plan_year: planYear }) => planYear === selectedPlanYear
  )

  const memberIchraConfig = member?.ichra_configs?.find(
    ({ plan_year: planYear }) => planYear === selectedPlanYear
  )

  return (
    <BrokersMembersViewErrorBoundary planYear={selectedPlanYear}>
      <BrokersMemberContext.Provider
        value={{ ...props, planYear: selectedPlanYear, employerIchraConfig }}
      >
        <div className='flex flex-col space-y-4'>
          {/* Header with breadcrumbs and plan year selector */}
          <div className='flex items-center justify-between'>
            <div className='flex flex-col space-x-2'>
              {enabledFeatures.includes(BROKERS_MEMBERS_VIEW_FEATURES.BREADCRUMBS) && (
                <Breadcrumbs>
                  <Breadcrumbs.Item href={brokersEmployers.index()}>
                    Employers
                  </Breadcrumbs.Item>
                  <Breadcrumbs.Item
                    href={brokersEmployees.index({ employer_id: employerId })}
                  >
                    {employerName}
                  </Breadcrumbs.Item>
                  <Breadcrumbs.Item current>
                    {memberName}
                  </Breadcrumbs.Item>
                </Breadcrumbs>
              )}
              <div className='heading-lg'>{memberName}</div>
            </div>
            <BrokersMembersPlanYearSelector
              isLoading={isLoading}
              selectedPlanYear={selectedPlanYear}
              setSelectedPlanYear={setSelectedPlanYear}
              availablePlanYears={availablePlanYears}
            />
          </div>

          {enabledFeatures.includes(BROKERS_MEMBERS_VIEW_FEATURES.MEMBER_SUMMARY) && (
            <BrokersMembersSummaryInfo
              memberId={memberId}
              employerId={employerId}
            />
          )}

          {enabledFeatures.includes(BROKERS_MEMBERS_VIEW_FEATURES.MEMBER_INFORMATION) && (
            <MemberInformation memberId={memberId} />
          )}

          {enabledFeatures.includes(BROKERS_MEMBERS_VIEW_FEATURES.ICHRA_AFFORDABILITY) && (
            <MemberIchraAffordability memberId={memberId} />
          )}

          {enabledFeatures.includes(BROKERS_MEMBERS_VIEW_FEATURES.UPLOADS) && (
            <BrokersMembersUploadsView memberId={memberId} />
          )}

          {enabledFeatures.includes(BROKERS_MEMBERS_VIEW_FEATURES.COVERED_PARTICIPANTS) && (
            <CoveredParticipantsView
              memberId={memberId}
              hasEmployerICHRAConfig={!!employerIchraConfig}
            />
          )}

          {enabledFeatures.includes(BROKERS_MEMBERS_VIEW_FEATURES.ENROLLMENTS) && (
            <BrokersMembersEnrollmentsSummary
              memberId={memberId}
              isTieredAllowancesEmployer={isTieredAllowancesEmployer}
              isUserThatchAdmin={isUserThatchAdmin}
            />
          )}

          {enabledFeatures.includes(BROKERS_MEMBERS_VIEW_FEATURES.ENROLLMENTS) && (
            <BrokersMembersEnrollments
              memberId={memberId}
            />
          )}

          {enabledFeatures.includes(BROKERS_MEMBERS_VIEW_FEATURES.STRIPE_CARDS) && (
            <StripeCardsMembersView memberId={memberId} />
          )}

          {enabledFeatures.includes(BROKERS_MEMBERS_VIEW_FEATURES.STRIPE_CARDS) && (
            <FinancialAccountsMembersView memberId={memberId} />
          )}

          {enabledFeatures.includes(BROKERS_MEMBERS_VIEW_FEATURES.CARDS) && (
            <CardsMembersView memberId={memberId} />
          )}

          {enabledFeatures.includes(BROKERS_MEMBERS_VIEW_FEATURES.MEMBER_ICHRA_CONFIG) &&
            memberIchraConfig && (
              <MemberIchraConfig member_ichra_config={memberIchraConfig} />
          )}

          {enabledFeatures.includes(BROKERS_MEMBERS_VIEW_FEATURES.TASKS) && (
            <TasksMembersView memberId={memberId} />
          )}

          {enabledFeatures.includes(BROKERS_MEMBERS_VIEW_FEATURES.QUALIFYING_LIFE_EVENTS) && (
            <QualifyingLifeEventsView
              memberId={memberId}
            />
          )}

          {enabledFeatures.includes(BROKERS_MEMBERS_VIEW_FEATURES.NOTES) && (
            <MemberNotes memberId={memberId} />
          )}
        </div>
      </BrokersMemberContext.Provider>
    </BrokersMembersViewErrorBoundary>
  )
}

const BrokersMembersView = (props: BrokersMembersViewProps) => {
  const {
    accessRequestResourceName,
    currentBroker,
    currentUserId,
    enabledFeatures,
  } = props
  const requireAccessGrant = enabledFeatures.includes(BROKERS_MEMBERS_VIEW_FEATURES.REQUIRE_ACCESS_GRANT)

  const { mutate: createAccessRequest } = usePostAccessRequests()
  const [isRequestingAccess, setIsRequestingAccess] = useState(false)

  const {
    data: accessRequestList, // AccessRequestsAccessRequestList
    error: accessRequestsLoadError,
    isLoading: isLoadingAccessRequests,
    refetch: refetchAccessRequests,
  } = useGetAccessRequests({
    'filter[only_allowing_immediate_access]': true,
    'filter[requested_by_ids][]': [currentUserId],
    'filter[requested_resources][]': [accessRequestResourceName],
    'filter[request_states][]': ['granted'],
    'page[size]': 1,
  }, {
    query: {
      enabled: requireAccessGrant,
    },
  })
  const accessRequests = accessRequestList?.data || []

  const NUM_OF_REQUESTED_DAYS_OF_ACCESS = 7
  const MS_PER_DAY = 24 * 60 * 60 * 1000
  const submitRequestForAccess = (reason: string) => {
    if (isRequestingAccess) return

    setIsRequestingAccess(true)
    const endTime = new Date(
      Date.now() + (NUM_OF_REQUESTED_DAYS_OF_ACCESS * MS_PER_DAY)
    )
    createAccessRequest({
      data: {
        requested_end_time: endTime.toISOString(),
        requested_reason: reason,
        requested_resource: accessRequestResourceName,
      }
    }, {
      onSuccess: async () => {
        await refetchAccessRequests()
        setIsRequestingAccess(false)
      },
      onError: () => {
        toast.error('Failed to submit access request')
        setIsRequestingAccess(false)
      },
    })
  }

  if (isLoadingAccessRequests || isRequestingAccess) {
    const loadingMessage = isRequestingAccess ? 'Requesting access...' : 'Verifying permissions...'
    return (
      <div className='flex flex-col justify-center items-center h-64'>
        <div className='mb-8 text-gray-500'>
          {loadingMessage}
        </div>
        <LoadingSpinnerCentered height='50px' width='50px' />
      </div>
    )
  }

  if (requireAccessGrant && accessRequestsLoadError) {
    return (
      <div className='flex flex-col gap-2 justify-center items-center h-64'>
        <SolidAlertTriangleIcon className='mb-3 text-orange-400' size={48} />
        <p>An error occurred while trying to load the page.</p>
        <p>Please try refreshing the page.</p>
      </div>
    )
  }

  // We retrieved access requests using only_allowing_immediate_access. So,
  // if we got _any_ back, we can access the page. Otherwise, we need to
  // request access.
  if (requireAccessGrant && accessRequests.length < 1) {
    return (
      <BrokersMembersViewReasonForAccess
        disabled={isLoadingAccessRequests || isRequestingAccess}
        numberOfDaysOfAccess={NUM_OF_REQUESTED_DAYS_OF_ACCESS}
        onSubmitReasonForAccess={submitRequestForAccess}
      />
    )
  }

  return (
    <BrokerContext.Provider value={{ currentBroker }}>
      <BrokersMembersViewImpl {...props} />
    </BrokerContext.Provider>
  )
}

export default BrokersMembersView
